import { useEffect, useRef, useState } from 'react';
import { useMessageGetter } from '@messageformat/react';
import { numberToFinnishString } from '../util/numberFormatter';

interface Props {
  finalSum: number;
  totalSaving: number;
  bonusSum: number;
  isPersonCustomer: boolean;
  enabled: boolean;
}

export const ProfitAndSavingsAnnouncer = (props: Props): JSX.Element => {
  const { finalSum, totalSaving, bonusSum, isPersonCustomer, enabled } = props;
  const [announcement, setAnnouncement] = useState('');
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const announceDelayRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const getTranslations = useMessageGetter('');
  const ANNOUNCEMENT_DURATION_MS = 3000;
  const ANNOUNCEMENT_DELAY_MS = 1000;

  useEffect(() => {
    if (!enabled) {
      setAnnouncement('');
      return undefined;
    }

    const profit = finalSum - totalSaving;
    let text = `${getTranslations(
      'profitAndSavings_estimatedSavings',
    )} ${numberToFinnishString(finalSum)} €. `;
    text += `${getTranslations('profitAndSavings_savings')} ${numberToFinnishString(
      totalSaving,
    )} €. `;
    text += `${getTranslations(
      'profitAndSavings_estimatedProfit',
    )} ${numberToFinnishString(profit)} €.`;

    if (isPersonCustomer) {
      text += ` ${getTranslations('profitAndSavings_opBonus')} ${numberToFinnishString(
        bonusSum,
      )} €.`;
    }

    if (announceDelayRef.current) {
      clearTimeout(announceDelayRef.current);
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    setAnnouncement('');

    announceDelayRef.current = setTimeout(() => {
      setAnnouncement(text);

      timeoutRef.current = setTimeout(() => {
        setAnnouncement('');
      }, ANNOUNCEMENT_DURATION_MS);
    }, ANNOUNCEMENT_DELAY_MS);

    return () => {
      if (announceDelayRef.current) {
        clearTimeout(announceDelayRef.current);
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalSum, totalSaving, bonusSum, isPersonCustomer, enabled]);

  return (
    <div className="sr-only" aria-live="polite" aria-atomic="true" role="status">
      {announcement}
    </div>
  );
};
